import React, { useState } from 'react';
import { Monitor, Smartphone, Tablet, Maximize2, Minimize2 } from 'lucide-react';

/**
 * Device Preview Component
 *
 * Features:
 * - Preview template in different device sizes
 * - Mobile (375px), Tablet (768px), Desktop (1440px)
 * - Fullscreen mode
 * - Responsive iframe
 */
const DevicePreview = ({
    previewUrl,
    templateNumber,
    isVisible = true,
    onClose
}) => {
    const [selectedDevice, setSelectedDevice] = useState('desktop');
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Device configurations
    const devices = {
        mobile: {
            name: 'Mobile',
            icon: Smartphone,
            width: 375,
            height: 667,
            label: 'iPhone SE'
        },
        tablet: {
            name: 'Tablet',
            icon: Tablet,
            width: 768,
            height: 1024,
            label: 'iPad'
        },
        desktop: {
            name: 'Desktop',
            icon: Monitor,
            width: 1440,
            height: 900,
            label: 'Desktop HD'
        }
    };

    const currentDevice = devices[selectedDevice];

    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${
                isFullscreen ? 'p-0' : 'p-8'
            }`}
            style={{ zIndex: 'var(--z-modal)' }}
        >
            <div
                className="bg-white flex flex-col"
                style={{
                    borderRadius: isFullscreen ? '0' : 'var(--radius-lg)',
                    maxWidth: isFullscreen ? '100%' : '95vw',
                    maxHeight: isFullscreen ? '100%' : '95vh',
                    width: isFullscreen ? '100%' : 'auto',
                    height: isFullscreen ? '100%' : 'auto',
                    boxShadow: isFullscreen ? 'none' : 'var(--shadow-2xl)'
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-4 border-b"
                    style={{ borderColor: 'var(--color-gray-200)' }}
                >
                    <div>
                        <h3
                            className="text-lg font-semibold"
                            style={{
                                color: 'var(--color-gray-900)',
                                fontFamily: 'var(--font-primary)'
                            }}
                        >
                            Preview Template {templateNumber}
                        </h3>
                        <p className="text-sm" style={{ color: 'var(--color-gray-500)' }}>
                            {currentDevice.label} - {currentDevice.width} × {currentDevice.height}px
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Device Selector */}
                        <div className="flex gap-1 p-1 bg-gray-100" style={{ borderRadius: 'var(--radius-md)' }}>
                            {Object.entries(devices).map(([key, device]) => {
                                const Icon = device.icon;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedDevice(key)}
                                        className="p-2 transition-colors"
                                        style={{
                                            borderRadius: 'var(--radius-sm)',
                                            backgroundColor:
                                                selectedDevice === key
                                                    ? 'var(--color-white)'
                                                    : 'transparent',
                                            color:
                                                selectedDevice === key
                                                    ? 'var(--color-primary)'
                                                    : 'var(--color-gray-600)',
                                            boxShadow:
                                                selectedDevice === key ? 'var(--shadow-sm)' : 'none'
                                        }}
                                        title={device.name}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </button>
                                );
                            })}
                        </div>

                        {/* Fullscreen Toggle */}
                        <button
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="p-2 hover:bg-gray-100 transition-colors"
                            style={{ borderRadius: 'var(--radius-sm)' }}
                            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? (
                                <Minimize2 className="w-5 h-5" style={{ color: 'var(--color-gray-600)' }} />
                            ) : (
                                <Maximize2 className="w-5 h-5" style={{ color: 'var(--color-gray-600)' }} />
                            )}
                        </button>

                        {/* Close Button */}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="ml-2 px-4 py-2 hover:bg-gray-100 transition-colors font-medium"
                                style={{
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--color-gray-700)'
                                }}
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>

                {/* Preview Area */}
                <div
                    className="flex-1 flex items-center justify-center p-8 bg-gray-100 overflow-auto"
                    style={{ backgroundColor: 'var(--color-gray-100)' }}
                >
                    <div
                        className="bg-white shadow-lg"
                        style={{
                            width: isFullscreen ? '100%' : `${currentDevice.width}px`,
                            height: isFullscreen ? '100%' : `${currentDevice.height}px`,
                            maxWidth: isFullscreen ? '100%' : '100%',
                            maxHeight: isFullscreen ? '100%' : '100%',
                            borderRadius: isFullscreen ? '0' : 'var(--radius-md)',
                            boxShadow: isFullscreen ? 'none' : 'var(--shadow-xl)',
                            overflow: 'hidden'
                        }}
                    >
                        <iframe
                            src={previewUrl || `/template${templateNumber}`}
                            className="w-full h-full border-0"
                            title={`Template ${templateNumber} Preview`}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                        />
                    </div>
                </div>

                {/* Footer Info */}
                {!isFullscreen && (
                    <div
                        className="px-6 py-3 border-t text-center text-sm"
                        style={{
                            borderColor: 'var(--color-gray-200)',
                            color: 'var(--color-gray-600)',
                            backgroundColor: 'var(--color-gray-50)'
                        }}
                    >
                        Preview is rendered in real-time. Changes may take a moment to reflect.
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevicePreview;
